import { useMemo } from "react";
import { useGroupedAgentExecutions, type PlainAgentExecution } from "@/queries/agent-queries";
import { useListEnvironments, type PlainEnvironment } from "@/queries/environment-queries";
import { EnvironmentPhase, ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { mostRecentAgentExecution } from "@/utils/agent-executions";

export type AgentExecutionWithContext = {
    execution: PlainAgentExecution;
    environment: PlainEnvironment | undefined;
};

export const useActiveAgentExecutions = (): {
    filteredExecutions: AgentExecutionWithContext[];
    isPending: boolean;
    isError: boolean;
} => {
    const { data: groupedData, isPending, isError } = useGroupedAgentExecutions();
    const { data: environmentsData } = useListEnvironments({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });

    const filteredExecutions = useMemo(() => {
        if (!groupedData.environmentScoped || !environmentsData?.environments) {
            return [];
        }

        // Create maps for quick lookup
        const environmentsMap = new Map(environmentsData.environments.map((env) => [env.id, env]));

        const result: AgentExecutionWithContext[] = [];

        // Iterate through environment-scoped executions
        groupedData.environmentScoped.forEach((executions, environmentId) => {
            const environment = environmentsMap.get(environmentId);

            // Only include executions for non-deleted environments
            if (
                environment &&
                environment.spec?.desiredPhase !== EnvironmentPhase.DELETED &&
                environment.status?.phase !== EnvironmentPhase.DELETING
            ) {
                // Only add the most recent agent execution for this environment
                result.push({
                    execution: mostRecentAgentExecution(executions),
                    environment,
                });
            }
        });

        return result;
    }, [groupedData.environmentScoped, environmentsData?.environments]);

    return {
        filteredExecutions,
        isPending,
        isError,
    };
};
